/**
 * Image-level scale and measuring on the Excalidraw board (maths in
 * web/shared/src/measure.ts, mirrored on the server in apps/takeoff/scale.py).
 *
 * - A drawing image carries `customData.scale` (natural-pixel units) and, when
 *   it came from a PDF, `customData.source.page_pt` (printed sheet size, for
 *   presets). Setting a scale locks the image so it can't be nudged while
 *   measuring; its 📏 badge (this overlay) unlocks / changes it.
 * - Runs (W) and areas (A) are Excalidraw lines tagged as fixtures of a linear
 *   / area item, or as plain measurements; their lengths are drawn here, live,
 *   in the scale of the drawing under them.
 * - Two-click calibration (⇧C) and PDF drop (each page → a drawing image with
 *   its sheet size) live here too.
 */
import type { AppState, BinaryFileData, DataURL, ExcalidrawImperativeAPI } from '@excalidraw/excalidraw/types';
import type { FileId } from '@excalidraw/excalidraw/element/types';
import { useEffect, useMemo, useRef, useState } from 'react';
import { loadPdf } from '../../shared/src/pdf';
import {
  centroid,
  convertLength,
  dimensionText,
  formatArea,
  formatLength,
  isAreaShape,
  measureOf,
  measureOnImage,
  midpointAlong,
  parseLength,
  polylineLength,
  scaleOf,
  scenePoints,
  sourceOf,
  toViewport,
  topLeft,
  type GeomEl,
  type MeasureDisplay,
  type Pt,
} from '../../shared/src/measure';
import { alongRunLabel, fixtureOf, type FixtureData } from '../../shared/src/estimate';
import { calibrationStep, dimensionBanner, lengthUnitSuffix, measuringBanner, snapAxis } from '../../shared/src/measureTools';
import { FONT, textElement, type El } from './elements';

const nonce = () => Math.floor(Math.random() * 2 ** 31);

// ---- natural image size -------------------------------------------------------------------

const naturalCache = new Map<string, Promise<[number, number]>>();

/** The image file's own pixel size (crop rectangles carry it; otherwise decode the file once). */
export function naturalSize(xapi: ExcalidrawImperativeAPI, el: GeomEl): Promise<[number, number] | null> {
  if (el.crop?.naturalWidth && el.crop.naturalHeight) return Promise.resolve([el.crop.naturalWidth, el.crop.naturalHeight]);
  const fileId = el.fileId;
  if (!fileId) return Promise.resolve(null);
  const f = xapi.getFiles()[fileId];
  if (!f?.dataURL) return Promise.resolve(null);
  let p = naturalCache.get(fileId);
  if (!p) {
    p = new Promise<[number, number]>((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve([img.naturalWidth, img.naturalHeight]);
      img.onerror = () => reject(new Error('image decode failed'));
      img.src = f.dataURL;
    });
    naturalCache.set(fileId, p);
    p.catch(() => naturalCache.delete(fileId));
  }
  return p.catch(() => null);
}

// ---- what gets a length label ---------------------------------------------------------------

export interface MeasuredLabel {
  id: string;
  text: string;
  at: [number, number];
  kind: 'linear' | 'area';
  /** No scaled drawing under it. */
  noScale: boolean;
  spans: boolean;
}

/** Linear / area fixtures and plain measurements, plus the run being drawn. */
export function measuredElements(scene: readonly GeomEl[], draftId: string | null): { el: GeomEl; kind: 'linear' | 'area' }[] {
  const out: { el: GeomEl; kind: 'linear' | 'area' }[] = [];
  for (const el of scene) {
    if (el.isDeleted) continue;
    const f = fixtureOf(el);
    if (f && f.measure === 'count' && f.along_run && (el.type === 'line' || el.type === 'arrow')) {
      out.push({ el, kind: 'linear' }); // counted along the run: its label says how many
      continue;
    }
    if (f && (f.measure === 'linear' || f.measure === 'area')) {
      // Typed values are shown by the fixture's own label; container symbols aren't drawn runs.
      if ((f.measure === 'linear' && f.length) || (f.measure === 'area' && f.area)) continue;
      if (f.measure === 'linear' && el.type !== 'line' && el.type !== 'arrow') continue;
      out.push({ el, kind: f.measure });
      continue;
    }
    const m = measureOf(el);
    if (m) out.push({ el, kind: m.measure });
    else if (el.id === draftId) out.push({ el, kind: isAreaShape(el) ? 'area' : 'linear' });
  }
  return out;
}

export function labelsFor(
  scene: readonly GeomEl[],
  draftId: string | null,
  display: MeasureDisplay,
  /** TYPE code of a fixture, for "count along the run" labels ("LX01 ×20 (23.06 m ÷ 1.2 m)"). */
  codeOf?: (f: FixtureData) => string | null,
): MeasuredLabel[] {
  return measuredElements(scene, draftId).flatMap(({ el, kind }) => {
    const pts = scenePoints(el);
    if (pts.length < 2 || polylineLength(pts) === 0) return [];
    const at = kind === 'area' ? centroid(pts) : midpointAlong(pts);
    const m = measureOnImage(scene, el, kind);
    const f = fixtureOf(el);
    const along = f?.measure === 'count' ? f.along_run : undefined;
    const text = along
      ? m
        ? alongRunLabel(codeOf?.(f!) ?? '', convertLength(m.value, m.unit, along.unit), along).trim()
        : `${codeOf?.(f!) ?? ''} no scale`.trim()
      : (() => {
          // Runs / areas of a type are tagged with its code ("L02 23.06 m"); plain measurements show the value only.
          const code = f?.role === 'fixture' ? codeOf?.(f) ?? '' : '';
          const value = m ? (kind === 'area' ? formatArea(m.value, m.unit, display) : formatLength(m.value, m.unit, display)) : 'no scale';
          return code ? `${code} ${value}` : value;
        })();
    return [{ id: el.id, text, at, kind, noScale: !m, spans: !!m?.spans }];
  });
}

/** Display name of an image for the panel ("49354-29A p.2", "drawing"). */
export function imageName(el: GeomEl): string {
  const s = sourceOf(el);
  if (s?.title) return `${s.title.replace(/\.pdf$/i, '')}${s.page ? ` p.${s.page}` : ''}`;
  return 'drawing';
}

// ---- overlay ---------------------------------------------------------------------------------

export interface CalibrationState {
  /** null until the user clicks the drawing to scale (Set scale with nothing selected). */
  imageId: string | null;
  pts: Pt[];
}

const CAL_UNIT_KEY = 'xb.calibrate.unit';
function readCalUnit(): 'ft' | 'm' | null {
  try {
    const v = localStorage.getItem(CAL_UNIT_KEY);
    return v === 'ft' || v === 'm' ? v : null;
  } catch {
    return null;
  }
}
export function rememberCalUnit(u: 'ft' | 'm') {
  try {
    localStorage.setItem(CAL_UNIT_KEY, u);
  } catch {
    /* private window */
  }
}

export function MeasureOverlay(p: {
  xapi: ExcalidrawImperativeAPI;
  /** Bumped on every Excalidraw onChange (camera, scene). */
  tick: number;
  /** Changes only with the scene: badges and labels are measured once per scene, projected per frame. */
  sceneKey?: string;
  display: MeasureDisplay;
  /** TYPE code for a placed fixture (count-along-the-run labels). */
  fixtureCode?: (f: FixtureData) => string | null;
  draftId: string | null;
  calibration: CalibrationState | null;
  readOnly: boolean;
  onBadge(imageId: string): void;
  /** Unlock a locked drawing that has no scale (its 🔒 badge). */
  onUnlock?(imageId: string): void;
  onCalibrationPoint(pt: Pt): void;
  /** W / A banner: what is being measured (null = not measuring). */
  measuring: { kind: 'linear' | 'area'; what: string | null } | null;
  /** Fixture tool banner: the armed type (null = off). */
  placing: string | null;
  onCalibrationDone(length: { value: number; unit: 'ft' | 'm' }): void;
  onCalibrationCancel(): void;
  /** Dimension tool: clicks so far (null = off). */
  dimension?: { pts: Pt[] } | null;
  onDimensionPoint?(pt: Pt): void;
  onDimensionCancel?(): void;
}) {
  const { xapi, calibration } = p;
  const st = xapi.getAppState();
  const scene = xapi.getSceneElements() as unknown as GeomEl[];
  const vp = (pt: Pt) => toViewport(st, pt);

  // Calibration clicks: capture pointer-downs on the canvas so Excalidraw doesn't select or move anything.
  const [hover, setHover] = useState<Pt | null>(null);
  const calRef = useRef(calibration);
  calRef.current = calibration;
  const cb = useRef(p);
  cb.current = p;
  useEffect(() => {
    if (!calibration) return;
    const host = document.querySelector('.xb-canvas .excalidraw');
    if (!host) return;
    const toScene = (e: PointerEvent): Pt => {
      const a = xapi.getAppState();
      const r = (host as HTMLElement).getBoundingClientRect();
      const z = a.zoom.value;
      const pt: Pt = [(e.clientX - r.left) / z - a.scrollX, (e.clientY - r.top) / z - a.scrollY];
      const c = calRef.current;
      return c && c.pts.length === 1 && e.shiftKey ? snapAxis(c.pts[0], pt) : pt;
    };
    const down = (ev: Event) => {
      const e = ev as PointerEvent;
      const t = e.target as HTMLElement | null;
      if (!t || t.tagName !== 'CANVAS') return;
      if (e.button !== 0) return; // middle / right drag still pans
      if ((calRef.current?.pts.length ?? 0) >= 2) return;
      e.preventDefault();
      e.stopPropagation();
      cb.current.onCalibrationPoint(toScene(e));
    };
    const move = (ev: Event) => {
      const c = calRef.current;
      if (!c || c.pts.length !== 1) return;
      setHover(toScene(ev as PointerEvent));
    };
    const key = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') {
        ev.preventDefault();
        ev.stopPropagation();
        cb.current.onCalibrationCancel();
      }
    };
    host.addEventListener('pointerdown', down, true);
    host.addEventListener('pointermove', move, true);
    window.addEventListener('keydown', key, true);
    return () => {
      host.removeEventListener('pointerdown', down, true);
      host.removeEventListener('pointermove', move, true);
      window.removeEventListener('keydown', key, true);
      setHover(null);
    };
  }, [calibration?.imageId, xapi]); // eslint-disable-line react-hooks/exhaustive-deps

  // Dimension clicks: same capture as calibration (Excalidraw must not select or move anything).
  const dimRef = useRef(p.dimension ?? null);
  dimRef.current = p.dimension ?? null;
  const [dimHover, setDimHover] = useState<Pt | null>(null);
  const dimOn = !!p.dimension;
  useEffect(() => {
    if (!dimOn) return;
    const host = document.querySelector('.xb-canvas .excalidraw');
    if (!host) return;
    const toScene = (e: PointerEvent): Pt => {
      const a = xapi.getAppState();
      const r = (host as HTMLElement).getBoundingClientRect();
      const z = a.zoom.value;
      const pt: Pt = [(e.clientX - r.left) / z - a.scrollX, (e.clientY - r.top) / z - a.scrollY];
      const d = dimRef.current;
      return d && d.pts.length === 1 && e.shiftKey ? snapAxis(d.pts[0], pt) : pt;
    };
    const down = (ev: Event) => {
      const e = ev as PointerEvent;
      const t = e.target as HTMLElement | null;
      if (!t || t.tagName !== 'CANVAS' || e.button !== 0) return;
      e.preventDefault();
      e.stopPropagation();
      cb.current.onDimensionPoint?.(toScene(e));
    };
    const move = (ev: Event) => {
      if (dimRef.current?.pts.length !== 1) return;
      setDimHover(toScene(ev as PointerEvent));
    };
    const key = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') {
        ev.preventDefault();
        ev.stopPropagation();
        cb.current.onDimensionCancel?.();
      }
    };
    host.addEventListener('pointerdown', down, true);
    host.addEventListener('pointermove', move, true);
    window.addEventListener('keydown', key, true);
    return () => {
      host.removeEventListener('pointerdown', down, true);
      host.removeEventListener('pointermove', move, true);
      window.removeEventListener('keydown', key, true);
      setDimHover(null);
    };
  }, [dimOn, xapi]);
  const dimPreview = (() => {
    const d = p.dimension;
    if (!d || d.pts.length !== 1 || !dimHover) return null;
    const [a, b] = [d.pts[0], dimHover];
    const probe = { id: 'dim-preview', type: 'arrow', x: a[0], y: a[1], width: 0, height: 0, points: [[0, 0], [b[0] - a[0], b[1] - a[1]]] } as unknown as GeomEl;
    return { a: vp(a), b: vp(b), text: dimensionText(scene, probe, p.display) };
  })();

  const { badges, lockedOnly, labels } = useMemo(
    () => ({
      badges: scene.filter((e) => e.type === 'image' && !e.isDeleted && scaleOf(e)),
      // Locked drawings without a scale can't be clicked at all in Excalidraw: give them an unlock badge.
      lockedOnly: p.readOnly ? [] : scene.filter((e) => e.type === 'image' && !e.isDeleted && e.locked && !scaleOf(e)),
      labels: labelsFor(scene, p.draftId, p.display, p.fixtureCode),
    }),
    // No sceneKey (callers that don't track one) → every render, as before.
    [p.sceneKey ?? scene, p.readOnly, p.draftId, p.display, p.fixtureCode], // eslint-disable-line react-hooks/exhaustive-deps
  );
  return (
    <div className={`xb-measure${calibration ? ' is-calibrating' : ''}`} data-testid="measure-overlay">
      {badges.map((e) => {
        const [x, y] = vp(topLeft(e));
        const s = scaleOf(e)!;
        return (
          <button
            key={e.id}
            className={`xb-scale-badge${e.locked ? ' is-locked' : ''}`}
            style={{ left: x, top: y }}
            onClick={() => p.onBadge(e.id)}
            title={e.locked ? 'Scaled and locked — click to unlock or change the scale' : 'Scaled drawing — click to change the scale'}
            data-testid={`scale-badge-${e.id}`}
          >
            📏 {s.label}
            {e.locked ? ' 🔒' : ''}
          </button>
        );
      })}
      {lockedOnly.map((e) => {
        const [x, y] = vp(topLeft(e));
        return (
          <button
            key={e.id}
            className="xb-scale-badge is-locked"
            style={{ left: x, top: y }}
            onClick={() => p.onUnlock?.(e.id)}
            title="Locked — click to unlock"
            data-testid={`lock-badge-${e.id}`}
          >
            🔒 Locked
          </button>
        );
      })}
      {labels.map((l) => {
        const [x, y] = vp(l.at);
        return (
          <span
            key={l.id}
            className={`xb-measure-label${l.noScale ? ' is-unscaled' : ''}${l.spans ? ' is-warn' : ''}`}
            style={{ left: x, top: y }}
            title={l.spans ? 'Crosses two scaled drawings — measured on the one holding most of it' : l.noScale ? 'No scaled drawing under it' : undefined}
            data-testid={`measure-label-${l.id}`}
          >
            {l.text}
            {l.spans ? ' ⚠' : ''}
          </span>
        );
      })}
      {calibration ? <CalibrationUi xapi={xapi} st={st} c={calibration} hover={hover} onDone={p.onCalibrationDone} onCancel={p.onCalibrationCancel} /> : null}
      {!calibration && p.dimension ? (
        <>
          <div className="xb-cal-hint" data-testid="dimension-banner" data-step={p.dimension.pts.length}>
            {dimensionBanner(p.dimension.pts.length)}
          </div>
          <svg className="xb-cal-svg" width="100%" height="100%">
            {p.dimension.pts.map((q, i) => {
              const [x, y] = vp(q);
              return <circle key={i} cx={x} cy={y} r={4} />;
            })}
            {dimPreview ? <line x1={dimPreview.a[0]} y1={dimPreview.a[1]} x2={dimPreview.b[0]} y2={dimPreview.b[1]} className="is-preview" /> : null}
          </svg>
          {dimPreview ? (
            <span className="xb-measure-label" style={{ left: (dimPreview.a[0] + dimPreview.b[0]) / 2, top: (dimPreview.a[1] + dimPreview.b[1]) / 2 }} data-testid="dimension-preview">
              {dimPreview.text}
            </span>
          ) : null}
        </>
      ) : null}
      {!calibration && !p.dimension && p.measuring ? (
        <div className="xb-cal-hint" data-testid="measure-banner">
          {measuringBanner(p.measuring.kind, p.measuring.what)}
        </div>
      ) : null}
      {!calibration && !p.measuring && p.placing ? (
        <div className="xb-cal-hint" data-testid="placing-banner">
          Placing {p.placing} · click to place each one · 1–9 switches type · Esc to stop
        </div>
      ) : null}
    </div>
  );
}

function CalibrationUi(p: { xapi: ExcalidrawImperativeAPI; st: AppState; c: CalibrationState; hover: Pt | null; onDone(len: { value: number; unit: 'ft' | 'm' }): void; onCancel(): void }) {
  const { c, st } = p;
  const [text, setText] = useState(() => lengthUnitSuffix(readCalUnit()));
  const inputRef = useRef<HTMLInputElement | null>(null);
  const vp = (pt: Pt) => toViewport(st, pt);
  const parsed = parseLength(text);
  const pts = c.pts.map(vp);
  const step = calibrationStep(!!c.imageId, c.pts.length);
  const preview = c.pts.length === 1 && p.hover ? vp(p.hover) : null;
  useEffect(() => {
    if (c.pts.length !== 2) return;
    const el = inputRef.current;
    if (!el) return;
    el.focus();
    el.setSelectionRange(0, 0); // type the number before the pre-filled unit
  }, [c.pts.length]);
  return (
    <>
      <div className="xb-cal-hint xb-cal-steps" data-testid="calibrate-hint" data-step={step.step}>
        <span>{step.text}</span>
        {step.step < 3 ? <small> · zoom in for accuracy · Esc cancels</small> : null}
      </div>
      <svg className="xb-cal-svg" width="100%" height="100%">
        {pts.length === 2 ? <line x1={pts[0][0]} y1={pts[0][1]} x2={pts[1][0]} y2={pts[1][1]} /> : null}
        {preview ? <line x1={pts[0][0]} y1={pts[0][1]} x2={preview[0]} y2={preview[1]} className="is-preview" /> : null}
        {pts.map(([x, y], i) => (
          <circle key={i} cx={x} cy={y} r={5} />
        ))}
      </svg>
      {pts.length === 2 ? (
        <form
          className="xb-cal-form"
          style={{ left: pts[1][0] + 12, top: pts[1][1] + 12 }}
          onSubmit={(ev) => {
            ev.preventDefault();
            if (parsed) {
              rememberCalUnit(parsed.unit);
              p.onDone(parsed);
            }
          }}
          data-testid="calibrate-form"
        >
          <input
            ref={inputRef}
            className="rl-input"
            placeholder={`24'-0", 7.3 m, 288"`}
            value={text}
            onChange={(ev) => setText(ev.target.value)}
            data-testid="calibrate-length"
          />
          <button className="xb-btn xb-btn-primary" disabled={!parsed} type="submit" data-testid="calibrate-set">
            Set scale
          </button>
          <button className="xb-btn" type="button" onClick={p.onCancel}>
            Cancel
          </button>
        </form>
      ) : null}
    </>
  );
}

// ---- PDF drop ---------------------------------------------------------------------------------

const PDF_LONG = 3072;
const PDF_MAX_PIXELS = 12_000_000;
const PDF_MAX_PAGES = 24;
const PDF_GAP = 80;

const randomId = () => {
  const a = new Uint8Array(12);
  crypto.getRandomValues(a);
  return Array.from(a, (b) => b.toString(36).padStart(2, '0')).join('').slice(0, 20);
};

function blobToDataURL(b: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = () => reject(r.error);
    r.readAsDataURL(b);
  });
}

/**
 * Render each page of a dropped PDF to a drawing image laid out in a row at
 * `at` (scene), labelled "<file> — p.N", sized in points (1 board unit = 1 pt)
 * and tagged with its printed sheet size so printed-scale presets work.
 */
export async function dropPdf(
  xapi: ExcalidrawImperativeAPI,
  file: File,
  at: { x: number; y: number },
  opts: { frameAt(x: number, y: number): string | null; onProgress?(done: number, total: number): void },
): Promise<number> {
  const doc = await loadPdf(await file.arrayBuffer());
  const total = Math.min(doc.numPages, PDF_MAX_PAGES);
  const title = file.name;
  let x = at.x;
  const els: El[] = [];
  const files: BinaryFileData[] = [];
  try {
    for (let i = 0; i < total; i++) {
      const page = await doc.getPage(i);
      const vp1 = page.getViewport({ scale: 1 });
      const w = vp1.width;
      const h = vp1.height;
      const scale = Math.min(PDF_LONG / Math.max(w, h), Math.sqrt(PDF_MAX_PIXELS / (w * h)));
      const vp = page.getViewport({ scale });
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(vp.width);
      canvas.height = Math.round(vp.height);
      const ctx = canvas.getContext('2d')!;
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      await page.render({ canvasContext: ctx, viewport: vp, canvas } as never).promise;
      const blob = await new Promise<Blob | null>((r) => canvas.toBlob(r, 'image/webp', 0.85));
      const png = blob && blob.type === 'image/webp' ? blob : await new Promise<Blob | null>((r) => canvas.toBlob(r, 'image/png'));
      if (!png) continue;
      const fileId = `pdf${randomId()}` as FileId;
      const dataURL = (await blobToDataURL(png)) as DataURL;
      files.push({ id: fileId, dataURL, mimeType: png.type as BinaryFileData['mimeType'], created: Date.now(), lastRetrieved: Date.now() });
      const id = randomId();
      const frameId = opts.frameAt(x + w / 2, at.y + h / 2);
      els.push({
        id,
        type: 'image',
        x,
        y: at.y,
        width: w,
        height: h,
        angle: 0,
        strokeColor: 'transparent',
        backgroundColor: 'transparent',
        fillStyle: 'solid',
        strokeWidth: 1,
        strokeStyle: 'solid',
        roughness: 0,
        opacity: 100,
        groupIds: [],
        frameId,
        roundness: null,
        seed: nonce(),
        version: 1,
        versionNonce: nonce(),
        isDeleted: false,
        boundElements: null,
        updated: Date.now(),
        link: null,
        locked: false,
        fileId,
        status: 'pending',
        scale: [1, 1],
        crop: null,
        customData: { source: { kind: 'pdf', title, page: i + 1, page_pt: [w, h] } },
      });
      els.push({
        ...textElement(randomId(), { x, y: at.y - 40, text: `${title} — p.${i + 1}`, fontSize: 20, fontFamily: FONT.Brand, strokeColor: '#495057', frameId }),
        versionNonce: nonce(),
        seed: nonce(),
      });
      x += w + PDF_GAP;
      opts.onProgress?.(i + 1, total);
    }
  } finally {
    void doc.destroy();
  }
  if (!els.length) return 0;
  xapi.addFiles(files);
  const { restoreElements, CaptureUpdateAction } = await import('@excalidraw/excalidraw');
  const restored = restoreElements(els as never, null, { refreshDimensions: true, repairBindings: true });
  xapi.updateScene({ elements: [...xapi.getSceneElementsIncludingDeleted(), ...restored], captureUpdate: CaptureUpdateAction.IMMEDIATELY });
  return files.length;
}
